/**
 * 发票业务状态标签
 *
 * 状态文案与颜色集中在这里，避免各页面各写一版导致口径不一致。
 */

import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  InboxOutlined,
  RollbackOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { InvoiceStatus, INVOICE_STATUS_LABELS } from '@ilc/shared';

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'default',
  [InvoiceStatus.CHECKING]: 'processing',
  [InvoiceStatus.PENDING_REVIEW]: 'gold',
  [InvoiceStatus.REJECTED]: 'volcano',
  [InvoiceStatus.APPROVED]: 'lime',
  [InvoiceStatus.PUSHED]: 'cyan',
  [InvoiceStatus.BOOKED]: 'green',
  [InvoiceStatus.ARCHIVED]: 'blue',
  [InvoiceStatus.TERMINATED]: 'default',
};

const STATUS_ICONS: Record<InvoiceStatus, React.ReactNode> = {
  [InvoiceStatus.DRAFT]: <InboxOutlined />,
  [InvoiceStatus.CHECKING]: <SyncOutlined spin />,
  [InvoiceStatus.PENDING_REVIEW]: <ClockCircleOutlined />,
  [InvoiceStatus.REJECTED]: <RollbackOutlined />,
  [InvoiceStatus.APPROVED]: <CheckCircleOutlined />,
  [InvoiceStatus.PUSHED]: <FileSearchOutlined />,
  [InvoiceStatus.BOOKED]: <FileDoneOutlined />,
  [InvoiceStatus.ARCHIVED]: <InboxOutlined />,
  [InvoiceStatus.TERMINATED]: <StopOutlined />,
};

/** 状态说明：写清「下一步在谁那里」，列表页不用点进去也能看懂 */
const STATUS_HINTS: Partial<Record<InvoiceStatus, string>> = {
  [InvoiceStatus.DRAFT]: '已上传但尚未提交校验，经办人可继续补充信息',
  [InvoiceStatus.CHECKING]: '自动校验执行中（OCR / 企查查 / 税务局 / 合同比对）',
  [InvoiceStatus.PENDING_REVIEW]: '校验完成，等待财务初审人工确认',
  [InvoiceStatus.REJECTED]: '财务初审已退回，等待经办人处理后重新提交',
  [InvoiceStatus.APPROVED]: '初审通过，等待推送至 T+ 待办业务池',
  [InvoiceStatus.PUSHED]: '已推送至 T+ 待办业务池，等待财务在 T+ 端选择会计科目生成凭证',
  [InvoiceStatus.BOOKED]: 'T+ 已生成凭证并回写凭证号',
  [InvoiceStatus.ARCHIVED]: '已归档，可检索追溯',
  [InvoiceStatus.TERMINATED]: '流程已终止（作废或主动废弃）',
};

export interface InvoiceStatusTagProps {
  status: InvoiceStatus | null | undefined;
  showIcon?: boolean;
  withHint?: boolean;
}

export function InvoiceStatusTag({
  status,
  showIcon = true,
  withHint = true,
}: InvoiceStatusTagProps): React.ReactElement {
  if (!status) return <Tag>未知</Tag>;
  const label = INVOICE_STATUS_LABELS[status] ?? status;
  const tag = (
    <Tag color={STATUS_COLORS[status] ?? 'default'} icon={showIcon ? STATUS_ICONS[status] : undefined}>
      {label}
    </Tag>
  );
  const hint = STATUS_HINTS[status];
  if (!withHint || !hint) return tag;
  return <Tooltip title={hint}>{tag}</Tooltip>;
}

export default InvoiceStatusTag;
